import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ 
  onFilesSelected, 
  isProcessing = false, 
  acceptedTypes = ['.pdf', '.jpg', '.jpeg', '.png'],
  maxFileSize = 50 * 1024 * 1024, // 50MB
  className = '' 
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e?.dataTransfer?.items && e?.dataTransfer?.items?.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter === 1) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const files = Array.from(e?.dataTransfer?.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFiles(files);
    // Reset input value to allow selecting same files again
    e.target.value = '';
  };

  const handleFiles = (files) => {
    const validFiles = files?.filter(file => {
      const isValidType = acceptedTypes?.some(type => 
        file?.name?.toLowerCase()?.endsWith(type?.replace('.', ''))
      );
      const isValidSize = file?.size <= maxFileSize;
      return isValidType && isValidSize;
    });

    if (validFiles?.length > 0) {
      onFilesSelected(validFiles);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
          ${isDragOver 
            ? 'border-primary bg-primary/5 scale-[1.02]' 
            : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
          }
          ${isProcessing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef?.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes?.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={isProcessing}
        />

        <div className="flex flex-col items-center space-y-4">
          <div className={`
            w-16 h-16 rounded-full flex items-center justify-center transition-colors
            ${isDragOver ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
          `}>
            <Icon 
              name={isDragOver ? "Download" : "Upload"} 
              size={32} 
              className={isDragOver ? 'animate-bounce' : ''}
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">
              {isDragOver ? 'Drop files here' : 'Upload Invoice Files'}
            </h3>
            <p className="text-sm text-muted-foreground">
              Drag and drop your files here, or click to browse
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
            <span className="px-2 py-1 bg-muted rounded">PDF</span>
            <span className="px-2 py-1 bg-muted rounded">JPG</span>
            <span className="px-2 py-1 bg-muted rounded">PNG</span>
          </div>

          <p className="text-xs text-muted-foreground">
            Maximum file size: {formatFileSize(maxFileSize)}
          </p>

          {!isDragOver && (
            <Button
              variant="outline"
              size="sm"
              iconName="FolderOpen"
              iconPosition="left"
              disabled={isProcessing}
            >
              Browse Files
            </Button>
          )}
        </div>
      </div>
      {isProcessing && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Loader2" size={16} className="animate-spin" />
            <span>Processing files...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadZone;